import React, { useEffect, useState } from "react";
import { apiClient } from "@/lib/api";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger
} from "@/components/ui/alert-dialog";
import { Plus, Pencil, Trash2, Search, FileText, Eye } from "lucide-react";
import { toast } from "sonner";

const formatDate = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return isNaN(d.getTime()) ? iso : d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
};

const PackingListsPage = () => {
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await apiClient.get("/packing-lists");
      setItems(data);
    } catch {
      toast.error("Failed to load packing lists");
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const handleDelete = async (id) => {
    try {
      await apiClient.delete(`/packing-lists/${id}`);
      toast.success("Packing list deleted");
      load();
    } catch {
      toast.error("Delete failed");
    }
  };

  const filtered = items.filter((p) => {
    if (!q.trim()) return true;
    const t = q.toLowerCase();
    return (
      p.pl_ref?.toLowerCase().includes(t) ||
      p.customer_name?.toLowerCase().includes(t)
    );
  });

  return (
    <div className="space-y-6" data-testid="packing-lists-page">
      <div className="flex items-end justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold ep-text-primary">Packing Lists</h1>
          <p className="mt-1 text-sm text-slate-500">
            Prepare shipment packing lists on company letterhead — customer details auto-fill from your directory.
          </p>
        </div>
        <Button className="rounded-sm ep-amber hover:ep-amber" onClick={() => nav("/packing-lists/new")} data-testid="new-packing-list-button">
          <Plus className="mr-2 h-4 w-4" /> New Packing List
        </Button>
      </div>

      <Card className="rounded-sm border-[#E5E7EB] shadow-none">
        <div className="border-b border-[#E5E7EB] p-4">
          <div className="relative w-full md:w-80">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search PL number or customer…"
              className="rounded-sm pl-9"
              data-testid="packing-list-search-input"
            />
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="packing-lists-table">
            <thead>
              <tr className="border-b border-[#E5E7EB] bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-500">
                <th className="px-4 py-3">PL Number</th>
                <th className="px-4 py-3">Date</th>
                <th className="px-4 py-3">Customer</th>
                <th className="px-4 py-3 text-right">Items</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={5} className="px-4 py-10 text-center text-slate-400">Loading…</td></tr>
              ) : filtered.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-4 py-10 text-center text-slate-400">
                    {items.length === 0
                      ? "No packing lists yet. Create your first one."
                      : "No packing lists match your search."}
                  </td>
                </tr>
              ) : filtered.map((p) => (
                <tr key={p.id} className="border-b border-[#E5E7EB] hover:bg-slate-50" data-testid={`packing-list-row-${p.id}`}>
                  <td className="px-4 py-3 font-medium ep-text-primary">
                    <button className="flex items-center gap-2 hover:underline" onClick={() => nav(`/packing-lists/${p.id}`)}>
                      <FileText className="h-4 w-4 text-[#1E40AF]" /> {p.pl_ref}
                    </button>
                  </td>
                  <td className="px-4 py-3 text-slate-600">{formatDate(p.pl_date)}</td>
                  <td className="px-4 py-3 text-slate-700">{p.customer_name || "—"}</td>
                  <td className="px-4 py-3 text-right text-slate-500">{p.items?.length || 0}</td>
                  <td className="px-4 py-3">
                    <div className="flex justify-end gap-1">
                      <Button variant="outline" size="sm" className="h-8 rounded-sm text-xs" onClick={() => nav(`/packing-lists/${p.id}`)} data-testid={`view-packing-list-${p.id}`}>
                        <Eye className="mr-1 h-3.5 w-3.5" /> View / PDF
                      </Button>
                      <Button variant="ghost" size="icon" className="h-8 w-8 rounded-sm" onClick={() => nav(`/packing-lists/${p.id}/edit`)} data-testid={`edit-packing-list-${p.id}`}>
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-8 w-8 rounded-sm text-red-600 hover:text-red-700" data-testid={`delete-packing-list-${p.id}`}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent className="rounded-sm">
                          <AlertDialogHeader>
                            <AlertDialogTitle>Delete {p.pl_ref}?</AlertDialogTitle>
                            <AlertDialogDescription>This cannot be undone.</AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel className="rounded-sm">Cancel</AlertDialogCancel>
                            <AlertDialogAction onClick={() => handleDelete(p.id)} className="rounded-sm bg-red-600 hover:bg-red-700">Delete</AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
};

export default PackingListsPage;
